/* =====================================================================
   El catálogo de estados y municipios, cargado una vez por sesión
   =====================================================================
   POR QUE HAY UN CACHE Y NO ES OPTIMIZACION PREMATURA. Los 32 estados no
   cambian mientras la pestaña esté abierta —no cambian en décadas—, y el
   selector se pinta en cada ficha de cliente, de proveedor, de sucursal
   y en cada renglón de una importación. Sin esto, abrir una lista de
   veinte clientes son veinte llamadas idénticas.

   EL CACHE ES DE LA PESTAÑA Y NO DEL NAVEGADOR, a propósito. Guardarlo
   en `localStorage` obligaría a inventar cuándo caduca, y la respuesta
   honesta —«cuando el Congreso cree un estado»— no se puede escribir en
   un número de días. Una variable viva es exacta: nace con la pestaña y
   muere con ella.
   ===================================================================== */
import { api } from '../api';

let cacheEstados = null;
let cargando = null;
const cacheMunicipios = new Map();
const cacheCP = new Map();

/* Para las pruebas y para quien necesite forzar una recarga. */
export function olvidarCatalogo() {
  cacheEstados = null;
  cargando = null;
  cacheMunicipios.clear();
  cacheCP.clear();
}

/* =====================================================================
   DOS LLAMADAS A LA VEZ SON UNA SOLA PETICION
   =====================================================================
   Dos selectores montados en el mismo formulario piden los estados en el
   mismo instante, antes de que ninguna respuesta haya llegado. Sin la
   promesa compartida, el cache no sirve para nada en el único momento en
   que hace falta: el del primer pintado.
   ===================================================================== */
export async function estados() {
  if (cacheEstados) return cacheEstados;
  if (!cargando) {
    cargando = api.get('/geografia/estados')
      .then((filas) => { cacheEstados = filas; return filas; })
      .finally(() => { cargando = null; });
  }
  return cargando;
}

export async function municipios(claveEstado) {
  if (!claveEstado) return { estado: '', cargado: false, municipios: [] };
  if (cacheMunicipios.has(claveEstado)) return cacheMunicipios.get(claveEstado);
  const r = await api.get(
    `/geografia/municipios?estado=${encodeURIComponent(claveEstado)}`);
  cacheMunicipios.set(claveEstado, r);
  return r;
}

/* =====================================================================
   RECONOCER LO QUE YA ESTABA GUARDADO
   =====================================================================
   Es la misma pieza que `app/importador/geografia.py` tiene en el
   servidor, y está aquí por una razón concreta: cuando el selector se
   cablea en una ficha que YA tiene «N.L.» escrito, hay que poder
   preseleccionar Nuevo León sin ir a preguntar. Si no, abrir una ficha
   vieja enseñaría el desplegable en blanco y quien la guardara sin
   tocarlo borraría el estado que tenía.

   NO ADIVINA. Devuelve la clave o cadena vacía; lo que no reconoce se
   queda como texto, que es exactamente lo que hay hoy.
   ===================================================================== */
export function plegar(texto) {
  if (!texto) return '';
  return String(texto)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '');
}

/* Las mismas equivalencias que el servidor, y por los mismos motivos.
   Están escritas dos veces porque son dos programas distintos; la prueba
   `geografiaImportador.test.js` comprueba que no se separen. */
export const ALIAS = {
  DF: '09', DISTRITOFEDERAL: '09', MEXICODF: '09', CIUDADDEMEXICODF: '09',
  MEXICO: '15', EDOMEX: '15', EDODEMEXICO: '15', ESTADODEMEXICO: '15',
  EDOMEXICO: '15',
  COAHUILA: '05', MICHOACAN: '16', VERACRUZ: '30',
  NL: '19', BC: '02', BCS: '03', QROO: '23', QUINTANAROO: '23', SLP: '24',
};

export function indice(filas) {
  const mapa = new Map();
  for (const f of filas) {
    for (const forma of [f.clave, f.nombre, f.nombre_corto, f.abreviatura,
                         f.clave_sat]) {
      const k = plegar(forma);
      if (k && !mapa.has(k)) mapa.set(k, f.clave);
    }
  }
  for (const [forma, clave] of Object.entries(ALIAS)) {
    if (!mapa.has(forma)) mapa.set(forma, clave);
  }
  return mapa;
}

export function reconocer(texto, mapa) {
  return mapa.get(plegar(texto)) ?? '';
}

/* =====================================================================
   CAPA 2 · CINCO DIGITOS Y SALE LA DIRECCION
   =====================================================================
   El cache es por código postal y vive lo que la pestaña, igual que el
   de arriba: quien corrige un dígito y vuelve atrás no debería pagar dos
   peticiones por la duda.
   ===================================================================== */
export async function codigoPostal(cp) {
  if (!/^\d{5}$/.test(cp ?? '')) return null;
  if (cacheCP.has(cp)) return cacheCP.get(cp);
  const r = await api.get(`/geografia/codigo-postal/${cp}`);
  cacheCP.set(cp, r);
  return r;
}

export async function estadoDelCatalogo() {
  return api.get('/geografia/carga');
}
